import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// Normalized field rect: all coords in 0-1 range relative to page
const initialState = {
  // Document state
  documentModel: null, // { numPages, pages: [{ width, height, rotation }] }

  // Viewer state
  scale: 1.0,
  currentPage: 1,

  // Fields state (normalized coordinates)
  fields: [], // [{ id, type, page, x, y, w, h, props }]
  selectedFieldIds: [],

  // UI state
  selectedFieldType: null,
  isMovingField: false,
  showDebugOverlay: false,

  // Signer-aware field placement
  selectedSignerEmail: null, // Email of currently selected signer for field assignment
  signers: [], // Array of signers with their info

  // History state
  history: [],
  historyIndex: -1,
};

export const useEditorStore = create(
  devtools((set, get) => ({
    ...initialState,
    
    // Document actions
    setDocumentModel: (model) => set({ documentModel: model }),
    
    // Viewer actions
    setScale: (scale) => set({ scale: Math.max(0.5, Math.min(3.0, scale)) }),
    zoomIn: () => set((state) => ({ scale: Math.min(3.0, state.scale + 0.25) })),
    zoomOut: () => set((state) => ({ scale: Math.max(0.5, state.scale - 0.25) })),
    setCurrentPage: (page) => set({ currentPage: page }),
    
    // Field selection
    selectField: (fieldId) => set({ selectedFieldIds: [fieldId] }),
    selectMultipleFields: (fieldIds) => set({ selectedFieldIds: fieldIds }),
    clearSelection: () => set({ selectedFieldIds: [] }),
    
    // Field type selection (for placing new fields)
    setSelectedFieldType: (type) => set({ selectedFieldType: type }),

    // Signer-aware field placement
    // Use signerId (e.g., "signer_1") for field assignment, not email
    // This allows fields to be assigned before recipient emails are known
    setSelectedSignerEmail: (signerId) => set({ selectedSignerEmail: signerId }),
    setSigners: (signers) => set({ signers }),
    getSelectedSignerEmail: () => get().selectedSignerEmail,
    getSelectedSignerId: () => get().selectedSignerEmail, // Alias for clarity

    // Save state to history
    _saveToHistory: () => {
      const { fields, history, historyIndex } = get();
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(JSON.parse(JSON.stringify(fields)));
      set({
        history: newHistory.slice(-50), // Keep last 50 states
        historyIndex: Math.min(newHistory.length - 1, 49),
      });
    },
    
    // Field CRUD with history
    addField: (field) => {
      get()._saveToHistory();
      const maxZ = Math.max(0, ...get().fields.map(f => f.z || 0));
      const selectedSignerEmail = get().selectedSignerEmail;

      // Auto-assign field to selected signer if one is selected
      const fieldWithSigner = {
        ...field,
        props: {
          ...field.props,
          assignedTo: selectedSignerEmail || field.props?.assignedTo || '',
        }
      };

      set((state) => ({
        fields: [...state.fields, {
          ...fieldWithSigner,
          id: `field_${Date.now()}`,
          z: maxZ + 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }],
        // Keep selectedFieldType active so users can place multiple fields
        // of the same type in a row without reselecting from the sidebar.
      }));
    },
    
    updateField: (fieldId, updates) => {
      set((state) => ({
        fields: state.fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)),
      }));
    },
    
    updateFieldWithHistory: (fieldId, updates) => {
      get()._saveToHistory();
      get().updateField(fieldId, updates);
    },
    
    deleteField: (fieldId) => {
      get()._saveToHistory();
      set((state) => ({
        fields: state.fields.filter((f) => f.id !== fieldId),
        selectedFieldIds: state.selectedFieldIds.filter((id) => id !== fieldId),
      }));
    },
    
    // Undo/Redo
    undo: () => {
      const { history, historyIndex } = get();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        set({
          fields: JSON.parse(JSON.stringify(history[newIndex])),
          historyIndex: newIndex,
        });
      }
    },
    
    redo: () => {
      const { history, historyIndex } = get();
      if (historyIndex < history.length - 1) {
        const newIndex = historyIndex + 1;
        set({
          fields: JSON.parse(JSON.stringify(history[newIndex])),
          historyIndex: newIndex,
        });
      }
    },
    
    canUndo: () => get().historyIndex > 0,
    canRedo: () => get().historyIndex < get().history.length - 1,
    
    // Get fields for a specific page
    getFieldsForPage: (pageNum) => {
      return get().fields.filter((f) => f.page === pageNum);
    },
    
    // Debug
    toggleDebugOverlay: () => set((state) => ({ showDebugOverlay: !state.showDebugOverlay })),
    
    // Bulk load fields without history tracking (for restoring from API)
    loadFields: (fields) => set({ fields }),

    // Reset store to initial state
    resetStore: () => set(initialState),
  }))
);
